import { ClientLocationEnum, IClient } from 'types/clients.types';
import { Currency } from 'types/global.types';
import {
  InvoiceItem,
  InvoiceTypeEnum,
  WhyZeroVATEnum,
} from 'types/invoice.types';
import { RevenueItemUnit } from 'types/revenues.types';
import { VATType } from 'types/users.types';
import { TracingConfig } from 'utils/amplitude/amplitude.types';
import { cleanEmptyValues } from 'utils/helpers';
import { ItemsLocalOptionsCache, ItemsSubmitOptions } from '../useItems';

export const cleanInvoiceItem = (item: Partial<InvoiceItem>) => {
  // fix very old items with bad formatting
  return cleanEmptyValues(
    {
      ...item,
      unitAmountExclVAT:
        item.unitAmountExclVAT == null
          ? null
          : Math.round(item.unitAmountExclVAT),
      unitAmountInclVAT:
        item.unitAmountInclVAT == null
          ? null
          : Math.round(item.unitAmountInclVAT),
      quantity: item.quantity == null ? null : Math.round(item.quantity),
      discountPercentage:
        item.discountPercentage == null
          ? null
          : Math.round(item.discountPercentage * 10) / 10,
      unit: item.unit || RevenueItemUnit.items,
    },
    ['VATRate'],
  );
};

export const cleanInvoiceItems = (items: Partial<InvoiceItem>[]) => {
  return items.map((item) => cleanInvoiceItem(item));
};

// suggests a value based on the client location only
// which may be wrong if used alone, there is other factors that can affect the VAT rate other than the client location
export const suggestVATRateBasedOnClientLocation = (
  clientLocation: IClient['location'] | undefined,
  defaultRate?: number | null,
) => {
  const isClientExtraEU = clientLocation === ClientLocationEnum.extra_eu;
  const isClientIntraEU = clientLocation === ClientLocationEnum.intra_eu;

  if (isClientExtraEU || isClientIntraEU) {
    return 0;
  }
  return defaultRate ?? null;
};

// suggests a value based on the client location only
// which may be wrong if used alone, there is other factors that can affect the WhyZeroVAT reason other than the client location
export const suggestWhyZeroVATBasedOnClientLocation = (
  clientLocation: IClient['location'] | undefined,
  defaultReason?: WhyZeroVATEnum,
) => {
  const isClientExtraEU = clientLocation === ClientLocationEnum.extra_eu;
  const isClientIntraEU = clientLocation === ClientLocationEnum.intra_eu;

  if (isClientExtraEU) {
    return WhyZeroVATEnum.extra_eu_export;
  }
  if (isClientIntraEU) {
    return WhyZeroVATEnum.intra_eu_reverse_charge;
  }

  return defaultReason ?? null;
};

// must reflect the fields we care about in the form
// used to omit all other values we dont care about on creating the form defaults
export const itemFieldsNames = {
  _id: '_id',
  assetId: 'assetId',
  unitAmountExclVAT: 'unitAmountExclVAT',
  unitAmountInclVAT: 'unitAmountInclVAT',
  discountPercentage: 'discountPercentage',
  quantity: 'quantity',
  unit: 'unit',
  VATRate: 'VATRate',
  name: 'name',
  categoryId: 'categoryId',
  category: 'category',
  whyZeroVAT: 'whyZeroVAT',
  doesUnitPriceIncludeVAT: 'doesUnitPriceIncludeVAT',
  description: 'description',
  _priceItemId: '_priceItemId',
  _priceItem: '_priceItem',
};

export type ItemFormTracingEvents = Partial<{
  itemName: TracingConfig<'onFocus'>;
  itemSearch: TracingConfig<'onFocus' | 'onChange' | 'onEdit'>;
  quantity: TracingConfig<'onChange'>;
  category: TracingConfig<'onChange'>;
  amount: TracingConfig<'onFocus'>;
  description: TracingConfig<'onFocus'>;
  VATRate: TracingConfig<'onClick' | 'onChange'>;
  whyZeroVAT: TracingConfig<'onClick' | 'onChange'>;
  unit: TracingConfig<'onClick' | 'onChange'>;
  advancedSettings: TracingConfig<'onChange'>;
  unitPriceToggle: TracingConfig<'onChange'>;
  discount: TracingConfig<'onFocus'>;
  toggleApplyDiscount: TracingConfig<'onChange'>;
  addToPriceList: TracingConfig<'onChange'>;
  updateOnPriceList: TracingConfig<'onChange'>;
}>;

export type ItemFormProps = {
  defaultValues?: Partial<InvoiceItem>;
  localOptions?: ItemsLocalOptionsCache;
  onSubmit: (data: InvoiceItem, options: ItemsSubmitOptions) => Promise<any>;
  onCancel: () => void;
  onUpdateLocalOptions?: (options: Partial<ItemsLocalOptionsCache>) => void;
  context: {
    client?: IClient | null | undefined;
    documentDate?: string;
    user: {
      VATType: VATType;
    };
    currency?: Currency;
    language?: string;
  };
  categoryRevenueTypes: InvoiceTypeEnum | InvoiceTypeEnum[];
  tracing?: ItemFormTracingEvents;
};
